import React, { useState } from "react";
import { ResultadoControles } from "./ControlesPrevios";
import { pagosApi } from "./pagosApi";

const CANAL_LABELS = {
  SAS_FISERV: "SAS Fiserv",
  NARANJA: "Naranja",
  QR: "QR",
  AMBIGUO: "Ambiguo",
  DESCONOCIDO: "Desconocido",
};

const PREPARACION_LABELS = {
  LISTA_PARA_REVISION: "Lista para revisión",
  INCOMPLETA: "Incompleta",
  BLOQUEADA: "Bloqueada",
};

export const ETAPAS = [
  { key: "seleccionar", label: "Seleccionar lote" },
  { key: "procesar", label: "Procesar" },
  { key: "revisar", label: "Revisar" },
  { key: "controles", label: "Controles previos" },
  { key: "registrar", label: "Registrar revisión" },
];

const money = new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", minimumFractionDigits: 2 });
const formatMoney = (value) => (typeof value === "number" && Number.isFinite(value) ? money.format(value) : "No disponible");
const formatDate = (value) => {
  const [year, month, day] = String(value || "").slice(0, 10).split("-");
  return year && month && day ? `${day}/${month}/${year}` : "Sin fecha";
};

function Etapas({ actual }) {
  const index = ETAPAS.findIndex((etapa) => etapa.key === actual);
  return (
    <ol className="pagos-etapas" aria-label="Etapas del flujo de pagos">
      {ETAPAS.map((etapa, position) => (
        <li key={etapa.key} className={etapa.deshabilitada ? "is-disabled" : position < index ? "is-done" : position === index ? "is-current" : ""}>
          <span>{position + 1}</span>{etapa.label}{etapa.deshabilitada && <small>No habilitado</small>}
        </li>
      ))}
    </ol>
  );
}

export default function ProcesamientoSas({ token, persistencia, onRegistered }) {
  const [fecha, setFecha] = useState("");
  const [seleccion, setSeleccion] = useState(null);
  const [importacionId, setImportacionId] = useState(null);
  const [runId, setRunId] = useState("");
  const [txt, setTxt] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [declarations, setDeclarations] = useState({});
  const [loading, setLoading] = useState("");
  const [error, setError] = useState("");
  const [registrada, setRegistrada] = useState(false);

  const reset = () => { setResultado(null); setDeclarations({}); setRegistrada(false); setError(""); };

  const buscar = async () => {
    reset(); setSeleccion(null); setLoading("lotes");
    try {
      const response = await pagosApi.lotesSas(fecha, token);
      setSeleccion(response);
      setImportacionId(response?.importacionSugerida ?? null);
      setRunId(response?.corridaSugerida ?? "");
    } catch (requestError) {
      setError(requestError?.message || "No se pudieron consultar los lotes de la fecha.");
    } finally {
      setLoading("");
    }
  };

  const procesar = async (nextDeclarations = declarations) => {
    setError(""); setLoading("procesar");
    try {
      const pagosManuales = Object.entries(nextDeclarations).map(([grupoId, value]) => ({ grupoId, ...value }));
      // El navegador envía IDs, TXT opcional y declaraciones. Las operaciones/estados de la
      // corrida se recuperan en backend por runId; no se reenvía el preview del browser.
      const response = await pagosApi.procesarSas({ importacionId, runId: runId || null, txt, pagosManuales }, token);
      if (response?.mode !== "READ_ONLY_PREVIEW" || response?.persisted !== false) {
        throw new Error("El procesamiento no devolvió un resultado de solo lectura verificable.");
      }
      setResultado(response);
    } catch (requestError) {
      setResultado(null);
      setError(requestError?.message || "No se pudo procesar el lote.");
    } finally {
      setLoading("");
    }
  };

  const declarar = (groupId, value) => {
    const next = { ...declarations, [groupId]: value };
    setDeclarations(next);
    procesar(next);
  };

  const quitar = (groupId) => {
    const next = { ...declarations };
    delete next[groupId];
    setDeclarations(next);
    procesar(next);
  };

  const registrar = async () => {
    const pagosManuales = Object.entries(declarations).map(([grupoId, value]) => ({ grupoId, ...value }));
    const response = await pagosApi.registrarProcesamiento({
      importacionId, runId: runId || null, txt, pagosManuales,
    }, token);
    setRegistrada(true);
    if (onRegistered) onRegistered(response);
    return response;
  };

  const importaciones = seleccion?.importaciones || [];
  const corridas = seleccion?.corridas || [];
  const etapa = registrada ? "registrar" : resultado ? "controles" : importacionId ? "procesar" : "seleccionar";
  const origen = resultado?.origen;

  return (
    <section className="pagos-panel" aria-labelledby="pagos-sas-title" data-testid="procesamiento-sas">
      <div className="pagos-section-heading">
        <div>
          <h2 id="pagos-sas-title">Lote SAS y controles</h2>
          <p>Seleccioná la fecha, confirmá la importación sugerida y procesá. El FUR se reconstruye desde SQL.</p>
        </div>
        <span className="pagos-mode-badge">Solo lectura · importes históricos</span>
      </div>
      <Etapas actual={etapa} />

      <div className="pagos-filter-grid">
        <label className="pagos-date"><span>Fecha de pago</span>
          <input aria-label="Fecha de pago SAS" type="date" value={fecha}
            onChange={(event) => { setFecha(event.target.value); setSeleccion(null); setImportacionId(null); setRunId(""); reset(); }} />
        </label>
        <button type="button" className="pagos-primary" disabled={!fecha || loading === "lotes"} onClick={buscar}>
          {loading === "lotes" ? "Buscando…" : "Buscar lote"}
        </button>
      </div>
      {error && <div className="pagos-alert pagos-alert--error" role="alert">{error}</div>}

      {seleccion && <>
        {(seleccion.faltantes || []).length > 0 && (
          <div className="pagos-alert pagos-alert--warning" role="status"><strong>{importacionId ? "Fuentes no disponibles (podés procesar igual)" : "Falta para procesar"}</strong>
            <ul>{seleccion.faltantes.map((item) => <li key={item}>{item}</li>)}</ul>
            <p className="pagos-cell-note">La carga se hace con los módulos existentes:{" "}
              <a href="/administracion/importacion-operaciones">Importación Ops</a> (SAS) ·{" "}
              <a href="/administracion/conciliacion-operaciones">Conciliación Ops</a> (ZIP Fiserv y corridas).</p>
          </div>
        )}
        {(seleccion.advertencias || []).length > 0 && (
          <div className="pagos-alert" role="status"><ul>{seleccion.advertencias.map((item) => <li key={item}>{item}</li>)}</ul></div>
        )}

        <h3 className="pagos-subheading">Importaciones del {formatDate(seleccion.fecha)}</h3>
        <div className="pagos-table-wrap"><table aria-label="Importaciones de la fecha"><thead><tr>
          <th></th><th>Importación</th><th>Canal detectado</th><th>Filas insertadas</th><th>Tarjetas</th><th>Observaciones</th>
        </tr></thead><tbody>
          {!importaciones.length ? <tr><td colSpan="6"><div className="pagos-empty"><p>No hay importaciones para la fecha.</p></div></td></tr>
            : importaciones.map((item) => (
              <tr key={item.id} className={item.aptaSas ? "" : "pagos-row--muted"}>
                <td><input type="radio" name="importacion-sas" aria-label={`Elegir importación ${item.id}`} disabled={!item.aptaSas}
                  checked={importacionId === item.id} onChange={() => { setImportacionId(item.id); reset(); }} /></td>
                <td><strong>{item.id}</strong> · {item.archivo}<small className="pagos-cell-note">{item.tipoArchivo} · {item.proveedor} · {item.estado}</small></td>
                <td><span className={`pagos-status ${item.aptaSas ? "pagos-status--safe" : "pagos-status--warning"}`}>{CANAL_LABELS[item.canal] || item.canal}</span></td>
                <td>{item.filasInsertadas}</td>
                <td><small>{(item.tarjetas || []).slice(0, 4).map((t) => `${t.tarjeta} (${t.filas})`).join(" · ")}</small></td>
                <td><small>{[...(item.senales || []), ...(item.advertencias || [])].join(" ")}</small></td>
              </tr>
            ))}
        </tbody></table></div>

        <h3 className="pagos-subheading">Corridas de conciliación</h3>
        <div className="pagos-table-wrap"><table aria-label="Corridas de la fecha"><thead><tr>
          <th></th><th>Corrida</th><th>Operaciones Fiserv</th><th>Débito</th><th>Fuentes</th>
        </tr></thead><tbody>
          <tr>
            <td><input type="radio" name="corrida-sas" aria-label="Sin corrida" checked={!runId} onChange={() => { setRunId(""); reset(); }} /></td>
            <td colSpan="4">Sin corrida: la conciliación por comercio no se evalúa.</td>
          </tr>
          {corridas.map((run) => (
            <tr key={run.id} className={run.previewDisponible ? "" : "pagos-row--muted"}>
              <td><input type="radio" name="corrida-sas" aria-label={`Elegir corrida ${run.id}`} disabled={!run.previewDisponible}
                checked={runId === run.id} onChange={() => { setRunId(run.id); reset(); }} /></td>
              <td><strong>{run.archivoZip}</strong><small className="pagos-cell-note">ID {run.id} · {run.estado}</small></td>
              <td>{run.operacionesFiserv}</td>
              <td>{run.debitoCalculado ? "Calculado" : "Pendiente (Conciliación Ops)"}</td>
              <td><small>{(run.fuentes || []).map((f) => `${f.familia} ${f.producto} (${f.operaciones})`).join(" · ")}</small></td>
            </tr>
          ))}
        </tbody></table></div>

        <div className="pagos-filter-grid">
          <label className="pagos-date"><span>TXT ya generado (opcional, sólo para comparar)</span>
            <input aria-label="TXT para comparar" type="file" accept=".txt" onChange={(event) => { setTxt(event.target.files?.[0] || null); reset(); }} />
          </label>
          <button type="button" className="pagos-primary" disabled={!importacionId || loading === "procesar"} onClick={() => procesar()}>
            {loading === "procesar" ? "Procesando y controlando…" : "Procesar y ejecutar controles"}
          </button>
        </div>
      </>}

      {resultado && <>
        <section className="pagos-demo-dashboard" aria-label="Resumen real del lote">
          <div className="pagos-section-heading"><div><h3>Resumen del lote</h3>
            <p>Datos reales de la importación seleccionada y del FUR reconstruido.</p></div>
            <span className={`pagos-status ${resultado.preparacion.estado === "LISTA_PARA_REVISION" ? "pagos-status--safe" : "pagos-status--warning"}`}>
              {PREPARACION_LABELS[resultado.preparacion.estado] || resultado.preparacion.estado}</span></div>
          <div className="pagos-summary pagos-summary--demo">
            <div className="pagos-summary-card"><span>Fecha del lote</span><strong>{formatDate(resultado.resumen?.fecha || origen.fechaPago)}</strong><small>Importación {resultado.resumen?.importacionId || origen.importacionId}</small></div>
            <div className="pagos-summary-card"><span>Operaciones</span><strong>{resultado.resumen?.operaciones ?? origen.filasImportacion}</strong><small>{origen.filasEncontradas} vinculadas al histórico</small></div>
            <div className="pagos-summary-card"><span>Grupos FUR</span><strong>{resultado.resumen?.grupos ?? resultado.controles?.resumen?.grupos}</strong><small>{resultado.resumen?.comerciosUnicos ?? "—"} comercios únicos por CUIT</small></div>
            <div className="pagos-summary-card"><span>Bruto importación SAS</span><strong>{formatMoney(resultado.resumen?.brutoImportacion)}</strong><small>Todas las operaciones de la importación</small></div>
            <div className="pagos-summary-card"><span>Bruto grupos FUR</span><strong>{formatMoney(resultado.resumen?.bruto)}</strong><small>Mismo universo que descuentos y neto</small></div>
            <div className="pagos-summary-card"><span>Descuentos históricos</span><strong>{formatMoney(resultado.resumen?.descuentos)}</strong><small>Suma de descuentos de los grupos FUR</small></div>
            <div className="pagos-summary-card"><span>Neto histórico total</span><strong>{formatMoney(resultado.resumen?.netoHistorico ?? resultado.controles?.conciliacion?.totalFur)}</strong><small>Referencia base_dashboard</small></div>
            <div className="pagos-summary-card"><span>Grupos con incidencias</span><strong>{resultado.resumen?.comerciosConIncidencias ?? 0}</strong><small>No se confunde con comercios únicos</small></div>
          </div>
          <div className="pagos-source-grid">
            <div><strong>Fuentes disponibles</strong><p>{(resultado.resumen?.fuentesDisponibles || []).join(" · ") || "Sin fuentes informadas"}</p></div>
            <div><strong>Fuentes faltantes</strong><p>{(resultado.resumen?.fuentesFaltantes || []).join(" · ") || "Ninguna informada"}</p></div>
          </div>
        </section>

        <div className="pagos-alert pagos-alert--warning" role="note" aria-label="Origen de los importes">
          <strong>Importes históricos, no autónomos.</strong> {origen.nota}
          <small className="pagos-cell-note">Importación {origen.importacionId} · {origen.archivo} · {CANAL_LABELS[origen.canal] || origen.canal} · pago {formatDate(origen.fechaPago)} ·
            {" "}{origen.filasEncontradas}/{origen.filasImportacion} operaciones en base_dashboard
            {origen.comerciosNoPaga > 0 && ` · ${origen.comerciosNoPaga} comercios con neto ≤ 0 fuera del FUR (${formatMoney(origen.netoNoPaga)})`}</small>
        </div>

        <div className="pagos-reconciliation" aria-label="Preparación del FUR">
          <h3>Preparación del FUR · <span className={`pagos-status ${resultado.preparacion.estado === "LISTA_PARA_REVISION" ? "pagos-status--safe" : "pagos-status--warning"}`}>
            {PREPARACION_LABELS[resultado.preparacion.estado] || resultado.preparacion.estado}</span></h3>
          <ul>{resultado.preparacion.motivos.map((motivo) => <li key={motivo}>{motivo}</li>)}</ul>
          {resultado.corrida && <p className="pagos-cell-note">Corrida {resultado.corrida.id}: {resultado.corrida.operacionesFiserv} operaciones Fiserv ·
            fuentes {(resultado.corrida.fuentes || []).length} · faltantes {(resultado.fuentesFaltantes || []).join(", ") || "ninguna"}</p>}
        </div>

        <ResultadoControles result={resultado.controles} detalleGrupos={resultado.grupos || []} persistencia={persistencia} defaultDate={String(origen.fechaPago).slice(0, 10)}
          fechaFija onDeclare={declarar} onUndeclare={quitar} onRegister={registrar} />
      </>}
    </section>
  );
}
